<template lang="pug">
  b-modal#upsertPartnerMarket(
    ref="upsertPartnerMarket"
    :title="modalTitle"
    centered
    no-fade
    no-close-on-backdrop
    :busy="loading"
    size="sm"
    @ok="done"
    @show="reset"
  )
    b-form(novalidate :disabled="!submission.success" @submit.stop.prevent="submit")
      .form-group
        label {{ $t("ui.sports_type") }}
        DebounceInput(
          type="text"
          ref="sports_type"
          :placeholder='$t("ui.sports_type")'
          :value="sportsTypeLabel"
          readonly
        )
      .form-group
        label {{ $t("ui.market_type") }}
        DebounceInput(
          type="text"
          ref="market_type"
          :placeholder='$t("ui.market_type")'
          :value="marketTypeLabel"
          readonly
        )
      .form-group
        label.mr-1 {{ $t("ui.ldiff") }}
        i.fa.fa-info-circle(aria-hidden="true" :title='$t("ui.ldiff_tooltips")')
        DebounceInput(
          type="text"
          ref="ldiff"
          :dirty="$v.ldiff.$dirty"
          :error="$v.ldiff.$error"
          :placeholder='$t("ui.ldiff")'
          v-model.trim.lazy="$v.ldiff.$model"
          :disabled="loading"
        )
        ValidateResult(:value="$v.ldiff" :name="$t('ui.ldiff')" :min="ldiffMin" :max="ldiffMax" :errors="ldiffErrors")
      .form-group
        label.mr-1 {{ $t("ui.margin") }}
        i.fa.fa-info-circle(aria-hidden="true" :title='$t("ui.margin_tooltips")')
        DebounceInput(
          type="text"
          ref="margin"
          :dirty="$v.margin.$dirty"
          :error="$v.margin.$error"
          :placeholder='$t("ui.margin")'
          v-model.trim.lazy="$v.margin.$model"
          :disabled="loading"
        )
        ValidateResult(:value="$v.margin" :name="$t('ui.margin')" :min="marginMin" :max="marginMax" :errors="marginErrors")
      .form-group.mb-2
        small.text-muted
          i.fa.fa-info-circle.mr-1(:title='$t("ui.ldiff_margin_priority_tooltips")')
          | {{ $t("ui.ldiff_margin_priority") }}
      .form-group
        b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}
    div(slot="modal-cancel")
      SubmitButton(:loading="loading" icon="fa fa-times" :text="$t('ui.cancel')" isModal=true)
    div(slot="modal-ok")
      SubmitButton(:loading="loading" icon="fa fa-save" :text="$t('ui.save')" isModal=true)
</template>

<script>
import { required, helpers } from "vuelidate/lib/validators";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";

const isSingleDecimal = value => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return /^-?\d+(\.\d)?$/.test(String(value).trim());
};

const isIntegerValue = value => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return /^-?\d+$/.test(String(value).trim());
};

const betweenNumber = (min, max, valueChecker) =>
  helpers.withParams({ type: "between", min, max }, value => {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    if (!valueChecker(value)) {
      return false;
    }
    const num = Number(value);
    return num >= min && num <= max;
  });

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput
  },
  props: {
    selected: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      sports_type: "",
      sports_name: "",
      market_type: "",
      ldiff: "",
      margin: "",
      ldiffMin: -0.5,
      ldiffMax: 0.5,
      marginMin: 0,
      marginMax: 5,
      submission: {
        success: true,
        status: ""
      }
    };
  },
  validations() {
    return {
      ldiff: {
        required,
        isSingleDecimal,
        between: betweenNumber(this.ldiffMin, this.ldiffMax, isSingleDecimal)
      },
      margin: {
        required,
        isIntegerValue,
        between: betweenNumber(this.marginMin, this.marginMax, isIntegerValue)
      }
    };
  },
  computed: {
    modalTitle() {
      const market = this.marketTypeLabel || this.$t("ui.market_type");
      return this.$t("ui.edit") + " - " + market;
    },
    sportsTypeLabel() {
      if (this.sports_name) {
        return String(this.sports_name).toUpperCase();
      }
      return this.sports_type !== "" &&
        this.sports_type !== null &&
        this.sports_type !== undefined
        ? String(this.sports_type)
        : "";
    },
    marketTypeLabel() {
      const type = Number(this.market_type);
      if (type === 1) {
        return this.$t("ui.early");
      }
      if (type === 2) {
        return this.$t("ui.today");
      }
      if (type === 3) {
        return this.$t("ui.live");
      }
      return this.market_type !== "" &&
        this.market_type !== null &&
        this.market_type !== undefined
        ? String(this.market_type)
        : "";
    },
    ldiffErrors() {
      if (this.$v.ldiff.$dirty && this.$v.ldiff.isSingleDecimal === false) {
        return [this.$t("error.isSingleDecimal", { fname: this.$t("ui.ldiff") })];
      }
      return [];
    },
    marginErrors() {
      if (this.$v.margin.$dirty && this.$v.margin.isIntegerValue === false) {
        return [this.$t("error.isInteger", { fname: this.$t("ui.margin") })];
      }
      return [];
    }
  },
  methods: {
    done(evt) {
      evt.preventDefault();
      this.submit();
    },
    formatLdiff(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return String(value);
      }
      return String(Number(num.toFixed(1)));
    },
    formatMargin(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return String(value);
      }
      return String(Math.round(num));
    },
    reset() {
      this.$v.$reset();
      this.submission.success = true;
      this.submission.status = "";
      this.sports_type = this.selected.sports_type;
      this.sports_name =
        this.selected.sports_name ||
        this.$store.getters.getSelectedSportsName ||
        "";
      this.market_type = this.selected.market_type;
      this.ldiff = this.formatLdiff(this.selected.ldiff);
      this.margin = this.formatMargin(this.selected.margin);
    },
    cancel() {
      this.$refs.upsertPartnerMarket.hide();
    },
    submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;
        this.$swal({
          title: this.$t("ui.status"),
          text: this.$t("message.update_partner_market_confirm"),
          type: "warning",
          showCancelButton: true,
          allowOutsideClick: false
        }).then(result => {
          if (result.value) {
            this.upsertPartnerMarket();
          } else if (result.dismiss === "cancel") {
            this.loading = false;
            this.$swal({
              title: this.$t("ui.cancel"),
              text: this.$t("message.update_partner_market_cancel"),
              type: "error",
              allowOutsideClick: false
            });
          } else {
            this.loading = false;
          }
        });
      }
    },
    upsertPartnerMarket() {
      const sports_type = String(this.sports_type).trim();
      const market_type = String(this.market_type).trim();
      const ldiff = String(this.ldiff).trim();
      const margin = String(this.margin).trim();
      this.$store
        .dispatch("user/upsertPartnerMarket", {
          sports_type,
          market_type,
          ldiff,
          margin
        })
        .then(response => {
          this.loading = false;
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
          if (this.submission.success == true) {
            this.$swal({
              title: this.$t("ui.success"),
              text: this.$t("message.update_partner_market_success"),
              type: "success",
              allowOutsideClick: false
            }).then(() => {
              this.cancel();
              this.$emit("clicked");
            });
          }
        })
        .catch(response => {
          this.loading = false;
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
        });
    }
  }
};
</script>
